import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
      );
    }

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    console.log('disabled');
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    console.log('enabled');
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest('product-form-component')
    );

    const quantitySelector = productForm?.refs.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }

      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    let flyToCartClass = addToCartButton.classList.contains(
      'quick-add__button'
    )
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );
    flyToCartElement.style.setProperty('--start-opacity', '0');

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) =>
        clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const target = this.closest(
      '.shopify-section, dialog, product-card'
    );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="id"]')
      );

    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) =>
        item.variant_id.toString() ===
        variantIdInput.value.toString()
    );

    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {any | undefined} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="id"]')
      );

    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error(
        'Failed to fetch cart quantity:',
        error
      );

      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source ===
        'product-form-component'
    ) {
      return;
    }

    const cart = /** @type {Cart} */ (
      event.detail?.resource
    );

    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;

    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll('add-to-cart-component')
      );

    // Check if ANY add to cart button is disabled
    const anyButtonDisabled = Array.from(
      allAddToCartContainers
    ).some(
      (container) =>
        container.refs.addToCartButton?.disabled
    );

    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) {
      throw new Error('Product form element missing');
    }

    /*
    * Fixed variant forms must always submit their configured
    * variant, regardless of any external variant-picker event.
    */
    if (
      this.dataset.fixedVariantForm === 'true' &&
      this.dataset.fixedVariantId
    ) {
      const fixedVariantInput =
        form.querySelector('input[name="id"]');

      if (fixedVariantInput) {
        fixedVariantInput.value =
          this.dataset.fixedVariantId;

        console.log(
          'Submitting fixed variant:',
          this.dataset.fixedVariantId
        );
      }
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation =
        this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate =
          this.dataset.quantityErrorMax || '';

        const errorMessage = errorTemplate.replace(
          '{{ maximum }}',
          validation.maxQuantity?.toString() || ''
        );

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode =
              document.createTextNode(errorMessage);

            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;

            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    let cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }

      formData.append(
        'sections',
        cartItemComponentsSectionIds.join(',')
      );
    });

    const fetchCfg = fetchConfig('javascript', {
      body: formData,
    });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,

      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())

      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) return;

          addToCartTextError.classList.remove('hidden');

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode =
              document.createTextNode(
                response.message
              );

            addToCartTextError.appendChild(
              newTextNode
            );
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;

            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount:
                Number(formData.get('quantity')) ||
                Number(
                  this.dataset.quantityDefault
                ),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add(
              'hidden'
            );

            addToCartTextError.removeAttribute(
              'aria-live'
            );
          }

          if (!id) {
            throw new Error(
              'Form ID is required'
            );
          }

          const anyAddToCartButton =
            allAddToCartContainers[0]?.refs
              .addToCartButton;

          if (anyAddToCartButton) {
            const addedTextElement =
              anyAddToCartButton.querySelector(
                '.add-to-cart-text--added'
              );

            const addedText =
              addedTextElement?.textContent?.trim() ||
              Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch updated cart
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',

              itemCount:
                Number(formData.get('quantity')) ||
                Number(
                  this.dataset.quantityDefault
                ),

              productId: this.dataset.productId,

              sections: response.sections,
            })
          );
        }
      })

      .catch((error) => {
        console.error(error);
      })

      .finally(() => {
        cartPerformance.measureFromEvent(
          'add:user-action',
          event
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel.textContent?.match(
          /\((\d+)\s+(.+)\)/
        );

      if (inCartText && inCartText[2]) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;

    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;

    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */ (
          newElement.cloneNode(true)
        )
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    /*
    * Fixed upsell forms such as Bello Duo must never
    * follow the main product's variant selection.
    */
    if (this.dataset.fixedVariantForm === 'true') {
      const fixedVariantId = this.dataset.fixedVariantId;

      if (fixedVariantId && this.refs.variantId) {
        this.refs.variantId.value = fixedVariantId;
      }

      console.log(
        'Fixed variant form ignored main variant update:',
        fixedVariantId
      );

      return;
    }

    const eventProductId =
      event.detail.data.newProduct?.id ??
      event.detail.data.productId;

    console.log('Variant Update Fired', {
      eventProductId: eventProductId,
      datasetProductId: this.dataset.productId,
      eventProductIdType: typeof eventProductId,
      datasetProductIdType: typeof this.dataset.productId,
      variant: event.detail.resource,
      available: event.detail.resource?.available,
    });

    /*
    * If this product-form-component doesn't have data-product-id,
    * initialise it from the variant event.
    *
    * This is important when the theme has multiple product-form
    * components such as main ATC + sticky/secondary ATC.
    */
    if (!this.dataset.productId && eventProductId) {
      this.dataset.productId = String(eventProductId);

      console.log(
        'Product ID initialized:',
        this.dataset.productId
      );
    }

    /*
    * Handle product changes.
    */
    if (event.detail.data.newProduct?.id) {
      this.dataset.productId = String(
        event.detail.data.newProduct.id
      );
    }

    /*
    * Ignore only when BOTH IDs actually exist
    * and belong to different products.
    */
    if (
      eventProductId &&
      this.dataset.productId &&
      String(eventProductId) !==
        String(this.dataset.productId)
    ) {
      console.log(
        'Variant update ignored because product IDs do not match',
        {
          eventProductId: String(eventProductId),
          componentProductId:
            String(this.dataset.productId),
        }
      );

      return;
    }

    const { variantId } = this.refs;

    // Update selected variant ID
    variantId.value =
      event.detail.resource?.id ?? '';

    const {
      addToCartButtonContainer:
        currentAddToCartButtonContainer,

      acceleratedCheckoutButtonContainer,
    } = this.refs;

    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs
        .addToCartButton;

    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton &&
        !acceleratedCheckoutButtonContainer)
    ) {
      return;
    }

    /*
     * IMPORTANT FIX
     *
     * Morph the incoming button BEFORE changing its
     * disabled/enabled state.
     *
     * Shopify's returned HTML can contain the
     * "disabled" attribute. If enable() happens before
     * morph(), morph() can put disabled back.
     */
    const newAddToCartButton =
      event.detail.data.html.querySelector(
        'product-form-component [ref="addToCartButton"]'
      );

    if (
      newAddToCartButton &&
      currentAddToCartButton
    ) {
      // Preserve button content for custom Bello Duo button
      const isBelloDuoButton =
        currentAddToCartButton.classList.contains('bello-duo-button');

      const preservedContent = isBelloDuoButton
        ? currentAddToCartButton.innerHTML
        : null;

      // Allow Shopify to update variant/button attributes
      morph(
        currentAddToCartButton,
        newAddToCartButton
      );

      // Restore custom button text/content after morph
      if (isBelloDuoButton && preservedContent !== null) {
        currentAddToCartButton.innerHTML = preservedContent;
      }
    }

    /*
     * FINAL button state must be determined AFTER morph.
     */
    if (
      event.detail.resource == null ||
      event.detail.resource.available === false
    ) {
      console.log(
        'FINAL -> disabled',
        event.detail.resource
      );

      currentAddToCartButtonContainer.disable();
    } else {
      console.log(
        'FINAL -> enabled',
        event.detail.resource
      );

      currentAddToCartButtonContainer.enable();
    }

    /*
     * Accelerated checkout button
     */
    if (acceleratedCheckoutButtonContainer) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available === false
      ) {
        acceleratedCheckoutButtonContainer.setAttribute(
          'hidden',
          'true'
        );
      } else {
        acceleratedCheckoutButtonContainer.removeAttribute(
          'hidden'
        );
      }
    }

    /*
     * Update variant media
     */
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media
          ?.preview_image?.src;

      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    /*
     * Quantity / B2B updates
     */
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    const newQuantityInput =
      /** @type {HTMLInputElement | null} */ (
        event.detail.data.html.querySelector(
          'quantity-selector-component input[ref="quantityInput"]'
        )
      );

    if (
      quantitySelector?.updateConstraints &&
      newQuantityInput
    ) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const newQuantityRules =
      event.detail.data.html.querySelector(
        '.quantity-rules'
      );

    const isQuantityRulesChanging =
      !!quantityRules !== !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html.querySelector(
        'price-per-item'
      );

    const isPricePerItemChanging =
      !!pricePerItem !== !!newPricePerItem;

    if (
      (isQuantityRulesChanging ||
        isPricePerItemChanging) &&
      quantitySelector
    ) {
      const currentQuantityValue =
        quantitySelector.getValue?.();

      const newProductFormButtons =
        event.detail.data.html.querySelector(
          '.product-form-buttons'
        );

      if (
        productFormButtons &&
        newProductFormButtons
      ) {
        morph(
          productFormButtons,
          newProductFormButtons
        );

        const newQuantityInputElement =
          /** @type {HTMLInputElement | null} */ (
            event.detail.data.html.querySelector(
              'quantity-selector-component input[ref="quantityInput"]'
            )
          );

        if (
          this.refs.quantitySelector
            ?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          this.refs.quantitySelector.setValue(
            currentQuantityValue
          );

          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      const morphTargets = [
        [
          '.quantity-label',
          quantityLabel,
          quantitySelector,
        ],

        [
          '.quantity-rules',
          quantityRules,
          this.refs.productFormButtons,
        ],

        [
          'price-per-item',
          pricePerItem,
          quantitySelectorWrapper,
        ],
      ];

      for (
        const [
          selector,
          currentElement,
          fallback,
        ] of morphTargets
      ) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(
            selector
          ),
          fallback
        );
      }
    }

    /*
     * Volume pricing
     */
    const currentVolumePricing =
      this.refs.volumePricing;

    const newVolumePricing =
      event.detail.data.html.querySelector(
        'volume-pricing'
      );

    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch/update cart quantity for new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is
   * updating before #onVariantUpdate is called.
   */
  #onVariantSelected = (event) => {
    if (this.dataset.fixedVariantForm === 'true') {
      const fixedVariantId = this.dataset.fixedVariantId;

      if (fixedVariantId && this.refs.variantId) {
        this.refs.variantId.value = fixedVariantId;
      }

      return;
    }

    const eventProductId =
      event.detail?.data?.newProduct?.id ??
      event.detail?.data?.productId ??
      event.detail?.productId;

    console.log('Variant Selected', {
      eventProductId,
      datasetProductId: this.dataset.productId,
    });

    if (!this.dataset.productId && eventProductId) {
      this.dataset.productId =
        String(eventProductId);
    }

    if (
      eventProductId &&
      this.dataset.productId &&
      String(eventProductId) !==
        String(this.dataset.productId)
    ) {
      return;
    }

    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}